import type { InputMessage } from '../shared/datachannel';
import { adaptShortcutKey, type ShortcutMode } from '../shared/key-compat';
import type { PlatformKind } from '../shared/platform';
import { keyboardEventCandidates, keyFromKeyboardEvent } from '../shared/input/keyboard';

export { keyFromKeyboardEvent };

export interface KeyboardInputBinding {
  release(): void;
  dispose(): void;
}

export interface KeyboardInputOptions {
  getRemotePlatform?: () => PlatformKind;
  getShortcutMode?: () => ShortcutMode;
  // 连接控制端时，Ctrl/Cmd+V 先可靠地同步本机剪贴板，再触发远端粘贴。
  // 文本输入框属于 interactive target，不会走这里，仍保留浏览器本地粘贴。
  onPaste?: () => void | Promise<void>;
}

type InputSender = (msg: InputMessage) => void;

function suppressLocalKeyboardEvent(event: KeyboardEvent): void {
  event.preventDefault();
  // The key is already being forwarded to the remote peer. Stop local page
  // handlers (for example Escape closing the session toolbar) from acting on it.
  event.stopImmediatePropagation();
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  // 会话工具按钮不能成为系统键的“逃生口”：用户点完按钮后仍应能拦截 Win/Cmd，
  // 只有真正的文字编辑控件和明确标记为本地操作的按钮保留浏览器行为。
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"], [data-local-control="true"]'));
}

export function attachKeyboard(
  send: InputSender,
  isActive: () => boolean,
  options: KeyboardInputOptions = {},
): KeyboardInputBinding {
  const pressed = new Map<string, string>();

  const release = (): void => {
    for (const key of new Set(pressed.values())) {
      send({ type: 'keyup', key });
    }
    pressed.clear();
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!isActive() || isInteractiveTarget(event.target)) return;
    const candidates = keyboardEventCandidates(event);
    const sourceKey = candidates.find((candidate) => pressed.has(candidate)) || candidates[0] || '';
    if (!sourceKey) return;

    const shortcutMode = options.getShortcutMode?.() || 'auto';
    if (
      shortcutMode !== 'raw' &&
      !event.repeat &&
      options.onPaste &&
      (event.ctrlKey || event.metaKey) &&
      event.key.toLowerCase() === 'v'
    ) {
      suppressLocalKeyboardEvent(event);
      // 先释放已发送到远端的 Ctrl/Cmd，避免远端收到“修饰键仍按下”的残留状态；
      // 回调在收到被控端剪贴板回执后会按目标系统重新发送粘贴组合键。
      release();
      void options.onPaste();
      return;
    }

    // 浏览器会持续发送 repeat。远端注入的是合成键盘事件，很多系统不会
    // 像真实物理键盘一样自动产生按键重复，所以必须把 repeat keydown
    // 转发过去；否则长按方向键、退格键等只会在目标端触发一次。
    if (pressed.has(sourceKey)) {
      suppressLocalKeyboardEvent(event);
      if (event.repeat) {
        send({ type: 'keydown', key: pressed.get(sourceKey)!, repeat: true });
      }
      return;
    }
    suppressLocalKeyboardEvent(event);
    const key = adaptShortcutKey(
      sourceKey,
      options.getRemotePlatform?.() || 'unknown',
      shortcutMode,
    );
    pressed.set(sourceKey, key);
    send({ type: 'keydown', key });
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    const sourceKey = keyboardEventCandidates(event).find((candidate) => pressed.has(candidate)) || '';
    if (!sourceKey || !pressed.has(sourceKey)) return;
    suppressLocalKeyboardEvent(event);
    const key = pressed.get(sourceKey)!;
    pressed.delete(sourceKey);
    send({ type: 'keyup', key });
  };

  const onWindowBlur = (): void => release();
  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') release();
  };

  // 捕获阶段拦截 Ctrl/Alt/Meta 等浏览器快捷键，避免控制远端时误触本地页面。
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('keyup', onKeyUp, true);
  window.addEventListener('blur', onWindowBlur);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onWindowBlur);

  return {
    release,
    dispose: () => {
      release();
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onWindowBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onWindowBlur);
    },
  };
}
